import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import moment from 'moment-mini';
import Text from '@/components/Typography/Text';
import { formatUsageNumber as number } from './usageInsights';

const GAP = 4;
const TOOLTIP_WIDTH = 176;

export default function UsageDailyChart({ days, selectedDay, onSelectDay, border, muted, surface, creditsLabel }) {
  const [hoveredDay, setHoveredDay] = useState(null);
  const [focusedDay, setFocusedDay] = useState(null);
  const [width, setWidth] = useState(0);
  const maximum = Math.max(1, ...days.map(day => day.value));
  const activeIndex = days.findIndex(day => day.date === (hoveredDay || focusedDay));
  const activeDay = days[activeIndex];
  const barHeight = value => value > 0 ? Math.max(2, value / maximum * 152) : 0;
  const barWidth = Math.max(0, (width - GAP * (days.length - 1)) / days.length);
  const center = activeIndex * (barWidth + GAP) + barWidth / 2;
  const tooltipLeft = Math.max(0, Math.min(center - TOOLTIP_WIDTH / 2, width - TOOLTIP_WIDTH));

  return (
    <View onLayout={event => setWidth(event.nativeEvent.layout.width)} style={styles.chart}>
      <View pointerEvents="none" style={styles.grid}>
        {[0, 1, 2].map(line => <View key={line} style={{ height: StyleSheet.hairlineWidth, backgroundColor: border, opacity: 0.65 }} />)}
      </View>
      {days.map(day => (
        <Pressable
          key={day.date}
          accessibilityRole="button"
          accessibilityLabel={`${moment(day.date).format('MMM D')}: ${number(day.value)} ${creditsLabel}`}
          accessibilityState={{ selected: selectedDay === day.date }}
          onHoverIn={() => setHoveredDay(day.date)}
          onHoverOut={() => setHoveredDay(null)}
          onFocus={() => setFocusedDay(day.date)}
          onBlur={() => setFocusedDay(null)}
          onPress={() => onSelectDay(selectedDay === day.date ? null : day.date)}
          style={({ hovered, focused }) => [styles.day, {
            backgroundColor: hovered || focused || focusedDay === day.date || selectedDay === day.date ? muted : 'transparent',
          }]}
        >
          <View style={{
            height: barHeight(day.value),
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            backgroundColor: selectedDay && selectedDay !== day.date ? '#7D91B4' : '#548CFF',
          }} />
        </Pressable>
      ))}
      {activeDay && (
        <View
          pointerEvents="none"
          testID={`usage-day-tooltip-${activeDay.date}`}
          style={[styles.tooltip, {
            left: tooltipLeft,
            bottom: barHeight(activeDay.value) + 10,
            backgroundColor: surface,
            borderColor: border,
          }]}
        >
          <Text size="xs" color="secondary">{moment(activeDay.date).format('MMM D, YYYY')}</Text>
          <Text size="s" weight="bold">{number(activeDay.value)} {creditsLabel}</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  chart: { flexDirection: 'row', gap: GAP, marginTop: 8, marginBottom: 8, position: 'relative', zIndex: 1 },
  grid: { position: 'absolute', top: 8, bottom: 0, left: 0, right: 0, justifyContent: 'space-between' },
  day: { flex: 1, height: 160, justifyContent: 'flex-end', borderRadius: 4 },
  tooltip: {
    position: 'absolute', width: TOOLTIP_WIDTH, zIndex: 2,
    borderRadius: 8, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 9, gap: 4,
    shadowColor: '#000', shadowOpacity: 0.18, shadowRadius: 8, shadowOffset: { width: 0, height: 3 },
  },
});
